export interface TestStep {
  en: string;
  hu: string;
  de?: string;
  uk?: string;
}

export interface TestFeature {
  nameEn: string;
  nameHu: string;
  nameDe?: string;
  nameUk?: string;
  /** Feature ID for card display, e.g. "M1", "K6" */
  featureId?: string;
  /** Module name for badge, e.g. "Partner", "Bérlés", "Pénzügy" */
  module?: string;
  /** Navigation path for testing, e.g. "/partners" */
  navPath?: string;
  /** Navigation label hint, e.g. "Partner részletek" */
  navLabel?: string;
  /** Külső rendszer útvonala, ahol tesztelni kell — csak szövegként, NEM link (pl. DaForge `/admin/sugo`) */
  pathHint?: string | undefined;
  steps: TestStep[];
  expected: {
    en: string;
    hu: string;
    de?: string;
    uk?: string;
  };
}

export interface ReleaseTest {
  version: string;
  date: string;
  features: TestFeature[];
}

/**
 * Release test data — Insert new versions at the TOP of this array.
 * Target for bmad-release-update skill.
 */
export const releaseTests: ReleaseTest[] = [
  {
    version: '4.2.0',
    date: '2026-04-03',
    features: [
      {
        nameHu: 'Csomag kiválasztás a bérlési varázslóban',
        nameEn: 'Package Selection in Rental Wizard',
        nameDe: 'Paketauswahl im Miet-Assistenten',
        nameUk: 'Вибір пакету в майстрі оренди',
        steps: [
          {
            hu: 'Nyisd meg a Bérlés → Új bérlés varázslót',
            en: 'Open Rental → New Rental wizard',
            de: 'Öffne Vermietung → Neuer Miet-Assistent',
            uk: 'Відкрийте Оренда → Майстер нової оренди',
          },
          {
            hu: 'Válassz partnert, majd az Equipment lépésnél váltsd "Csomag" módra',
            en: 'Select a partner, then switch to "Package" mode in Equipment step',
            de: 'Partner auswählen, dann im Equipment-Schritt auf "Paket"-Modus wechseln',
            uk: 'Виберіть партнера, потім перемкніть на режим "Пакет" на кроці Обладнання',
          },
          {
            hu: 'Válaszd ki a "Fúrógép Csomag Pro" (PKG-DRILL-01) csomagot',
            en: 'Select "Drill Package Pro" (PKG-DRILL-01)',
            de: '"Bohrmaschinen-Paket Pro" (PKG-DRILL-01) auswählen',
            uk: 'Виберіть "Пакет свердла Pro" (PKG-DRILL-01)',
          },
          {
            hu: 'Ellenőrizd, hogy a Details lépésen megjelenik a bizomány szekció (fúrószár + vágókorong)',
            en: 'Verify consignment section appears in Details step (drill bits + cutting discs)',
            de: 'Überprüfe, ob der Konsignationsabschnitt im Details-Schritt erscheint',
            uk: "Перевірте, що секція консигнації з'являється на кроці Деталі",
          },
          {
            hu: 'A Summary lépésen ellenőrizd: bizomány tételek lista + INCLUDED tételek + bizomány érték a pricing-ban',
            en: 'In Summary step verify: consignment items list + INCLUDED items + consignment value in pricing',
            de: 'Im Zusammenfassungsschritt überprüfen: Konsignationsartikel + enthaltene Artikel + Konsignationswert',
            uk: 'На кроці Підсумок перевірте: список консигнаційних товарів + включені товари + вартість консигнації',
          },
        ],
        expected: {
          hu: 'A csomag tételei helyesen osztályozódnak (RENTAL/CONSIGNMENT/INCLUDED), az ár és a bizomány érték megjelenik.',
          en: 'Package items correctly classified (RENTAL/CONSIGNMENT/INCLUDED), price and consignment value displayed.',
          de: 'Paketartikel korrekt klassifiziert, Preis und Konsignationswert angezeigt.',
          uk: 'Товари пакету правильно класифіковані, ціна та вартість консигнації відображаються.',
        },
      },
      {
        nameHu: 'Bérlés létrehozás → automatikus bizomány kiadás',
        nameEn: 'Rental creation → automatic consignment dispatch',
        nameDe: 'Mieterstellung → automatische Konsignationsversendung',
        nameUk: 'Створення оренди → автоматична відправка консигнації',
        steps: [
          {
            hu: 'Hozz létre egy bérlést csomag módban (PKG-DRILL-01 vagy PKG-GRIND-01)',
            en: 'Create a rental in package mode (PKG-DRILL-01 or PKG-GRIND-01)',
            de: 'Erstelle eine Vermietung im Paketmodus',
            uk: 'Створіть оренду в режимі пакету',
          },
          {
            hu: 'A véglegesítés után ellenőrizd a Network tab-on: POST /consignments hívás történt-e',
            en: 'After finalization, check Network tab: was POST /consignments called',
            de: 'Nach Abschluss im Netzwerk-Tab prüfen: wurde POST /consignments aufgerufen',
            uk: 'Після завершення перевірте вкладку Мережа: чи був виклик POST /consignments',
          },
          {
            hu: 'Nyisd meg a bérlés részleteit és ellenőrizd, hogy a bizomány tételek megjelennek',
            en: 'Open rental details and verify consignment items appear',
            de: 'Mietdetails öffnen und Konsignationsartikel überprüfen',
            uk: 'Відкрийте деталі оренди та перевірте консигнаційні товари',
          },
        ],
        expected: {
          hu: 'A bérlés létrehozása után automatikusan létrejönnek a bizomány rekordok a szállítólevél számmal.',
          en: 'After rental creation, consignment records are automatically created with delivery note number.',
          de: 'Nach Mieterstellung werden Konsignationsdatensätze automatisch mit Lieferscheinnummer erstellt.',
          uk: 'Після створення оренди автоматично створюються записи консигнації з номером накладної.',
        },
      },
      {
        nameHu: 'Visszavétel → bizomány elszámolás',
        nameEn: 'Return → consignment settlement',
        nameDe: 'Rückgabe → Konsignationsabrechnung',
        nameUk: 'Повернення → розрахунок консигнації',
        steps: [
          {
            hu: 'Nyisd meg az R-2024-001 bérlés visszavételét (/rental/return?id=...)',
            en: 'Open R-2024-001 rental return (/rental/return?id=...)',
            de: 'Öffne R-2024-001 Mietrückgabe',
            uk: 'Відкрийте повернення оренди R-2024-001',
          },
          {
            hu: 'Az Inspect lépésen állítsd be az állapotot, majd kattints Következő',
            en: 'Set condition in Inspect step, then click Next',
            de: 'Zustand im Inspektionsschritt setzen, dann Weiter klicken',
            uk: 'Встановіть стан на кроці Огляд, потім натисніть Далі',
          },
          {
            hu: 'A Payment lépésen ellenőrizd: megjelenik a "Bizomány elszámolás" szekció 2 tétellel',
            en: 'In Payment step verify: "Consignment settlement" section appears with 2 items',
            de: 'Im Zahlungsschritt prüfen: Konsignationsabrechnungsabschnitt mit 2 Artikeln',
            uk: 'На кроці Оплата перевірте: секція розрахунку консигнації з 2 товарами',
          },
          {
            hu: 'Módosítsd a visszahozott mennyiséget (pl. fúrószár: 1-et hozz vissza a 2-ből)',
            en: 'Modify returned quantity (e.g. drill bits: return 1 of 2)',
            de: 'Zurückgegebene Menge ändern (z.B. 1 von 2 Bohrern zurück)',
            uk: 'Змініть повернуту кількість (напр. свердла: поверніть 1 з 2)',
          },
          {
            hu: 'Ellenőrizd, hogy a felszámított mennyiség és összeg automatikusan frissül',
            en: 'Verify charged quantity and amount update automatically',
            de: 'Überprüfe, dass berechnete Menge und Betrag automatisch aktualisiert werden',
            uk: 'Перевірте, що нарахована кількість та сума оновлюються автоматично',
          },
          {
            hu: 'Véglegesítsd a visszavételt és ellenőrizd, hogy a settle API hívás sikeres',
            en: 'Finalize return and verify settle API call succeeds',
            de: 'Rückgabe abschließen und Abrechnungs-API-Erfolg prüfen',
            uk: 'Завершіть повернення та перевірте успіх виклику API розрахунку',
          },
        ],
        expected: {
          hu: 'A bizomány tételek megjelennek, a mennyiségek módosíthatók, a settle API hibátlanul lefut.',
          en: 'Consignment items displayed, quantities editable, settle API runs without errors.',
          de: 'Konsignationsartikel angezeigt, Mengen bearbeitbar, Abrechnungs-API fehlerfrei.',
          uk: 'Консигнаційні товари відображаються, кількості редагуються, API розрахунку працює без помилок.',
        },
      },
      {
        nameHu: 'Szállítólevél PDF letöltés',
        nameEn: 'Delivery Note PDF Download',
        nameDe: 'Lieferschein-PDF-Download',
        nameUk: 'Завантаження PDF накладної',
        steps: [
          {
            hu: 'Az R-2024-001 visszavétel Payment lépésén keresd a "SZL" szállítólevél kártyát',
            en: 'In R-2024-001 return Payment step, find the "SZL" delivery note card',
            de: 'Im R-2024-001 Rückgabe-Zahlungsschritt die "SZL" Lieferscheinkarte finden',
            uk: 'На кроці Оплата повернення R-2024-001 знайдіть картку накладної "SZL"',
          },
          {
            hu: 'Kattints a "PDF" gombra az SZL-2026-0001 mellett',
            en: 'Click "PDF" button next to SZL-2026-0001',
            de: '"PDF"-Button neben SZL-2026-0001 klicken',
            uk: 'Натисніть кнопку "PDF" біля SZL-2026-0001',
          },
          {
            hu: 'Ellenőrizd, hogy megnyílik az A4 PDF: partner adatok, tételek táblázat, összeg, aláírás mezők',
            en: 'Verify A4 PDF opens: partner data, items table, totals, signature fields',
            de: 'Überprüfe A4-PDF: Partnerdaten, Artikeltabelle, Summen, Unterschriftsfelder',
            uk: 'Перевірте PDF A4: дані партнера, таблиця товарів, підсумки, поля підпису',
          },
        ],
        expected: {
          hu: 'A szállítólevél PDF megnyílik a böngészőben, tartalmazza a bizomány tételeket és az SZL számot.',
          en: 'Delivery note PDF opens in browser, contains consignment items and SZL number.',
          de: 'Lieferschein-PDF öffnet sich im Browser mit Konsignationsartikeln und SZL-Nummer.',
          uk: 'PDF накладної відкривається в браузері, містить консигнаційні товари та номер SZL.',
        },
      },
      {
        nameHu: 'Termék keresés a csomag szerkesztőben',
        nameEn: 'Product Search in Package Editor',
        nameDe: 'Produktsuche im Paket-Editor',
        nameUk: 'Пошук продуктів у редакторі пакетів',
        steps: [
          {
            hu: 'Nyisd meg az Admin Center → Csomagok → bármelyik csomag szerkesztése',
            en: 'Open Admin Center → Packages → edit any package',
            de: 'Admin Center → Pakete → beliebiges Paket bearbeiten öffnen',
            uk: 'Відкрийте Адмін-центр → Пакети → редагувати будь-який пакет',
          },
          {
            hu: 'A "Termék keresés" mezőbe írj be "Mak" (Makita)',
            en: 'Type "Mak" (Makita) in the "Product search" field',
            de: '"Mak" (Makita) in das "Produktsuche"-Feld eingeben',
            uk: 'Введіть "Mak" (Makita) у поле "Пошук продуктів"',
          },
          {
            hu: 'Ellenőrizd, hogy a találatok megjelennek (7 Makita termék)',
            en: 'Verify results appear (7 Makita products)',
            de: 'Überprüfe, dass Ergebnisse erscheinen (7 Makita-Produkte)',
            uk: "Перевірте, що з'являються результати (7 продуктів Makita)",
          },
        ],
        expected: {
          hu: 'A termék keresés működik, nem ad 400-as hibát, a Makita termékek megjelennek.',
          en: 'Product search works, no 400 error, Makita products displayed.',
          de: 'Produktsuche funktioniert, kein 400-Fehler, Makita-Produkte angezeigt.',
          uk: 'Пошук продуктів працює, немає помилки 400, продукти Makita відображаються.',
        },
      },
    ],
  },
  {
    version: '4.1.0',
    date: '2026-03-26',
    features: [
      {
        nameEn: 'rPI Delta — Search, Filters & CSV Export',
        nameHu: 'rPI Delta — Keresés, szűrők és CSV export',
        nameDe: 'rPI Delta — Suche, Filter & CSV-Export',
        nameUk: 'rPI Delta — Пошук, фільтри та CSV експорт',
        steps: [
          {
            en: 'Navigate to /admin-center/release-hub and select the Changelog tab',
            hu: 'Navigálj az /admin-center/release-hub oldalra és válaszd a Változásnapló fület',
            de: 'Navigiere zu /admin-center/release-hub und wähle den Changelog-Tab',
            uk: 'Перейдіть на /admin-center/release-hub та виберіть вкладку Журнал змін',
          },
          {
            en: 'Type a search term (e.g. "Delta") in the search bar and verify entries are filtered',
            hu: 'Írj be egy keresőkifejezést (pl. "Delta") a keresőmezőbe és ellenőrizd, hogy a bejegyzések szűrődnek',
            de: 'Gib einen Suchbegriff (z.B. "Delta") in die Suchleiste ein und überprüfe die Filterung',
            uk: 'Введіть пошуковий термін (напр. "Delta") у рядок пошуку та перевірте фільтрацію записів',
          },
          {
            en: 'Select a type filter (e.g. "feature") and verify only matching entries appear',
            hu: 'Válassz egy típusszűrőt (pl. "feature") és ellenőrizd, hogy csak a megfelelő bejegyzések jelennek meg',
            de: 'Wähle einen Typfilter (z.B. "feature") und überprüfe, dass nur passende Einträge angezeigt werden',
            uk: 'Виберіть фільтр типу (напр. "feature") та перевірте, що з\'являються лише відповідні записи',
          },
          {
            en: 'Select a category filter (e.g. "admin") and verify combined filtering works',
            hu: 'Válassz egy kategóriaszűrőt (pl. "admin") és ellenőrizd, hogy a kombinált szűrés működik',
            de: 'Wähle einen Kategoriefilter (z.B. "admin") und überprüfe die kombinierte Filterung',
            uk: 'Виберіть фільтр категорії (напр. "admin") та перевірте роботу комбінованої фільтрації',
          },
          {
            en: 'Click the CSV Export button and verify the downloaded file contains the filtered entries',
            hu: 'Kattints a CSV Export gombra és ellenőrizd, hogy a letöltött fájl tartalmazza a szűrt bejegyzéseket',
            de: 'Klicke auf den CSV-Export-Button und überprüfe die heruntergeladene Datei',
            uk: 'Натисніть кнопку CSV експорт та перевірте, що завантажений файл містить відфільтровані записи',
          },
        ],
        expected: {
          en: 'Search, type filter, category filter, and CSV export all function correctly with filtered results.',
          hu: 'A keresés, típusszűrő, kategóriaszűrő és CSV export mind helyesen működik a szűrt eredményekkel.',
          de: 'Suche, Typfilter, Kategoriefilter und CSV-Export funktionieren korrekt mit gefilterten Ergebnissen.',
          uk: 'Пошук, фільтр типу, фільтр категорії та CSV експорт працюють коректно з відфільтрованими результатами.',
        },
      },
      {
        nameEn: 'rPI Delta — 4-Language Support & Auto-Save',
        nameHu: 'rPI Delta — 4 nyelv támogatás és auto-mentés',
        nameDe: 'rPI Delta — 4-Sprachen-Unterstützung & Auto-Speichern',
        nameUk: 'rPI Delta — Підтримка 4 мов та автозбереження',
        steps: [
          {
            en: 'Navigate to /admin-center/release-hub and switch to the Release Testing tab',
            hu: 'Navigálj az /admin-center/release-hub oldalra és válts a Release Tesztelés fülre',
            de: 'Navigiere zu /admin-center/release-hub und wechsle zum Release-Testing-Tab',
            uk: 'Перейдіть на /admin-center/release-hub та переключіться на вкладку Тестування релізу',
          },
          {
            en: 'Switch language to DE (German) and verify all titles and steps display in German',
            hu: 'Válts nyelvet DE-re (német) és ellenőrizd, hogy az összes cím és lépés németül jelenik meg',
            de: 'Wechsle die Sprache auf DE (Deutsch) und überprüfe die deutsche Anzeige',
            uk: 'Переключіть мову на DE (німецька) та перевірте відображення німецькою',
          },
          {
            en: 'Switch language to UK (Ukrainian) and verify all titles and steps display in Ukrainian',
            hu: 'Válts nyelvet UK-ra (ukrán) és ellenőrizd, hogy az összes cím és lépés ukránul jelenik meg',
            de: 'Wechsle die Sprache auf UK (Ukrainisch) und überprüfe die ukrainische Anzeige',
            uk: 'Переключіть мову на UK (українська) та перевірте відображення українською',
          },
          {
            en: 'Check a test step checkbox, wait 2 seconds, then reload the page — verify the checkbox is still checked',
            hu: 'Jelölj be egy tesztelési lépés checkboxot, várj 2 másodpercet, majd töltsd újra az oldalt — ellenőrizd, hogy a checkbox még mindig be van jelölve',
            de: 'Aktiviere ein Testschritt-Kontrollkästchen, warte 2 Sekunden, lade die Seite neu — überprüfe den gespeicherten Zustand',
            uk: 'Позначте чекбокс тестового кроку, зачекайте 2 секунди, перезавантажте сторінку — перевірте збереження',
          },
          {
            en: 'Verify the tester name and completion timestamp appear next to the checked step',
            hu: 'Ellenőrizd, hogy a tesztelő neve és a befejezés időpontja megjelenik a bejelölt lépés mellett',
            de: 'Überprüfe, dass der Testername und Zeitstempel neben dem markierten Schritt angezeigt werden',
            uk: "Перевірте, що ім'я тестувальника та час завершення відображаються біля позначеного кроку",
          },
        ],
        expected: {
          en: 'All 4 languages display correctly, auto-save persists across page reloads, and tester attribution is shown.',
          hu: 'Mind a 4 nyelv helyesen jelenik meg, az auto-mentés megmarad oldal újratöltés után, és a tesztelő hozzárendelés látható.',
          de: 'Alle 4 Sprachen werden korrekt angezeigt, Auto-Speichern bleibt nach Neuladen erhalten, Tester-Zuordnung wird angezeigt.',
          uk: 'Усі 4 мови відображаються коректно, автозбереження зберігається після перезавантаження, атрибуція тестувальника показана.',
        },
      },
      {
        nameEn: 'Sprint Q — Supplier Import, Cross-sell & Transfer',
        nameHu: 'Sprint Q — Beszállítói import, cross-sell és átvezetés',
        nameDe: 'Sprint Q — Lieferantenimport, Cross-Selling & Übertragung',
        nameUk: 'Sprint Q — Імпорт постачальників, крос-продаж та трансфер',
        steps: [
          {
            en: 'Navigate to Inventory > Supplier Import and upload a sample CSV catalog file',
            hu: 'Navigálj a Készlet > Beszállítói import menübe és tölts fel egy minta CSV katalógus fájlt',
            de: 'Navigiere zu Inventar > Lieferantenimport und lade eine CSV-Katalogdatei hoch',
            uk: 'Перейдіть до Інвентар > Імпорт постачальників та завантажте зразковий CSV каталог',
          },
          {
            en: 'Verify imported items appear in the product catalog with correct supplier reference',
            hu: 'Ellenőrizd, hogy az importált tételek megjelennek a termékkatalógusban helyes beszállítói hivatkozással',
            de: 'Überprüfe, dass importierte Artikel mit korrekter Lieferantenreferenz im Produktkatalog erscheinen',
            uk: "Перевірте, що імпортовані товари з'являються в каталозі продукції з правильним посиланням на постачальника",
          },
          {
            en: 'Create a cross-sell invoice that includes items from multiple sales categories',
            hu: 'Hozz létre egy cross-sell számlát, amely több értékesítési kategóriából tartalmaz tételeket',
            de: 'Erstelle eine Cross-Selling-Rechnung mit Artikeln aus verschiedenen Verkaufskategorien',
            uk: 'Створіть рахунок крос-продажу з товарами з різних категорій продажу',
          },
          {
            en: 'Navigate to Inventory > Stock Transfer and initiate a transfer between two warehouses',
            hu: 'Navigálj a Készlet > Készletátvezetés menübe és indíts átvezetést két raktár között',
            de: 'Navigiere zu Inventar > Lagerübertragung und starte eine Übertragung zwischen zwei Lagern',
            uk: 'Перейдіть до Інвентар > Переміщення запасів та ініціюйте переміщення між двома складами',
          },
          {
            en: 'Verify stock levels updated correctly in both source and destination warehouses',
            hu: 'Ellenőrizd, hogy a készletszintek helyesen frissültek mind a forrás, mind a cél raktárban',
            de: 'Überprüfe die korrekte Aktualisierung der Bestände in Quell- und Ziellager',
            uk: 'Перевірте правильне оновлення рівнів запасів у вихідному та цільовому складах',
          },
        ],
        expected: {
          en: 'Supplier catalog imports correctly, cross-sell invoices handle mixed categories, and stock transfers update both warehouses.',
          hu: 'A beszállítói katalógus helyesen importálódik, a cross-sell számlák kezelik a vegyes kategóriákat, és az átvezetések frissítik mindkét raktárt.',
          de: 'Lieferantenkatalog importiert korrekt, Cross-Selling-Rechnungen verarbeiten gemischte Kategorien, Lagerübertragungen aktualisieren beide Lager.',
          uk: 'Каталог постачальників імпортується коректно, рахунки крос-продажу обробляють змішані категорії, переміщення оновлюють обидва склади.',
        },
      },
      {
        nameEn: 'Chatwoot & PIr Porter Integration',
        nameHu: 'Chatwoot és PIr Porter integráció',
        nameDe: 'Chatwoot & PIr Porter Integration',
        nameUk: 'Інтеграція Chatwoot та PIr Porter',
        steps: [
          {
            en: 'Navigate to Integrations > Chatwoot and verify the iframe loads the Chatwoot support panel',
            hu: 'Navigálj az Integrációk > Chatwoot menübe és ellenőrizd, hogy az iframe betölti a Chatwoot támogatási panelt',
            de: 'Navigiere zu Integrationen > Chatwoot und überprüfe, dass der Iframe das Support-Panel lädt',
            uk: 'Перейдіть до Інтеграції > Chatwoot та перевірте, що iframe завантажує панель підтримки',
          },
          {
            en: 'Navigate to Integrations > PIr Porter and verify the iframe loads the customer portal',
            hu: 'Navigálj az Integrációk > PIr Porter menübe és ellenőrizd, hogy az iframe betölti az ügyfélportált',
            de: 'Navigiere zu Integrationen > PIr Porter und überprüfe, dass der Iframe das Kundenportal lädt',
            uk: 'Перейдіть до Інтеграції > PIr Porter та перевірте, що iframe завантажує клієнтський портал',
          },
          {
            en: 'Verify both iframes are responsive and usable on tablet/desktop viewports',
            hu: 'Ellenőrizd, hogy mindkét iframe reszponzív és használható tablet/asztali nézetekben',
            de: 'Überprüfe, dass beide Iframes auf Tablet-/Desktop-Viewports responsiv und nutzbar sind',
            uk: 'Перевірте, що обидва iframe адаптивні та придатні для використання на планшеті/десктопі',
          },
          {
            en: 'Verify navigation back to ERP pages works correctly after using the iframe',
            hu: 'Ellenőrizd, hogy az ERP oldalakra való visszanavigálás helyesen működik az iframe használata után',
            de: 'Überprüfe die korrekte Navigation zurück zu ERP-Seiten nach Iframe-Nutzung',
            uk: 'Перевірте коректну навігацію назад до сторінок ERP після використання iframe',
          },
        ],
        expected: {
          en: 'Both Chatwoot and PIr Porter load correctly in iframes, are responsive, and ERP navigation is unaffected.',
          hu: 'A Chatwoot és a PIr Porter is helyesen töltődik be iframe-ekben, reszponzívak, és az ERP navigáció nem sérül.',
          de: 'Chatwoot und PIr Porter laden korrekt in Iframes, sind responsiv, und die ERP-Navigation bleibt unbeeinträchtigt.',
          uk: 'Chatwoot та PIr Porter коректно завантажуються в iframe, адаптивні, навігація ERP не порушена.',
        },
      },
      {
        nameEn: 'Helpdesk v3.0 ERP Integration',
        nameHu: 'Helpdesk v3.0 ERP integráció',
        nameDe: 'Helpdesk v3.0 ERP-Integration',
        nameUk: 'Інтеграція Helpdesk v3.0 з ERP',
        steps: [
          {
            en: 'Open a service worksheet in the ERP and click the Helpdesk link/button',
            hu: 'Nyiss meg egy szerviz munkalapot az ERP-ben és kattints a Helpdesk linkre/gombra',
            de: 'Öffne ein Service-Arbeitsblatt im ERP und klicke auf den Helpdesk-Link/-Button',
            uk: 'Відкрийте сервісний наряд в ERP та натисніть посилання/кнопку Helpdesk',
          },
          {
            en: 'Verify the Helpdesk opens with the correct worksheet context pre-filled',
            hu: 'Ellenőrizd, hogy a Helpdesk megnyílik a helyes munkalap kontextussal előre kitöltve',
            de: 'Überprüfe, dass das Helpdesk mit dem korrekten Arbeitsblatt-Kontext vorausgefüllt öffnet',
            uk: 'Перевірте, що Helpdesk відкривається з правильним попередньо заповненим контекстом наряду',
          },
          {
            en: 'Submit a helpdesk ticket from the worksheet context and verify it appears in the ticket list',
            hu: 'Küldj be egy helpdesk jegyet a munkalap kontextusból és ellenőrizd, hogy megjelenik a jegylistában',
            de: 'Erstelle ein Helpdesk-Ticket aus dem Arbeitsblatt-Kontext und überprüfe die Anzeige in der Ticketliste',
            uk: 'Надішліть тікет helpdesk з контексту наряду та перевірте його появу в списку тікетів',
          },
          {
            en: 'Verify the ticket references the original worksheet ID for traceability',
            hu: 'Ellenőrizd, hogy a jegy hivatkozik az eredeti munkalap azonosítóra a nyomon követhetőség érdekében',
            de: 'Überprüfe, dass das Ticket die ursprüngliche Arbeitsblatt-ID für die Rückverfolgbarkeit referenziert',
            uk: 'Перевірте, що тікет посилається на оригінальний ID наряду для відстежуваності',
          },
        ],
        expected: {
          en: 'Helpdesk opens with worksheet context, tickets are created with correct references, and appear in the ticket list.',
          hu: 'A Helpdesk a munkalap kontextusával nyílik meg, a jegyek helyes hivatkozásokkal jönnek létre és megjelennek a jegylistában.',
          de: 'Helpdesk öffnet mit Arbeitsblatt-Kontext, Tickets werden mit korrekten Referenzen erstellt und in der Ticketliste angezeigt.',
          uk: "Helpdesk відкривається з контекстом наряду, тікети створюються з правильними посиланнями та з'являються в списку тікетів.",
        },
      },
      {
        nameEn: '15% Discount Cap',
        nameHu: '15% kedvezmény felső határ',
        nameDe: '15%-Rabattobergrenze',
        nameUk: 'Верхня межа знижки 15%',
        steps: [
          {
            en: 'Navigate to a partner profile and set a discount percentage above 15%',
            hu: 'Navigálj egy partner profilra és állíts be 15% feletti kedvezmény százalékot',
            de: 'Navigiere zu einem Partnerprofil und setze einen Rabattprozentwert über 15%',
            uk: 'Перейдіть до профілю партнера та встановіть відсоток знижки вище 15%',
          },
          {
            en: 'Verify the system enforces the 15% maximum cap and displays a warning',
            hu: 'Ellenőrizd, hogy a rendszer érvényesíti a 15%-os maximumot és figyelmeztetést jelenít meg',
            de: 'Überprüfe, dass das System die 15%-Obergrenze durchsetzt und eine Warnung anzeigt',
            uk: 'Перевірте, що система застосовує максимальну межу 15% та відображає попередження',
          },
          {
            en: 'Create a rental with a discounted partner and verify the invoice total reflects exactly 15% discount',
            hu: 'Hozz létre egy bérlést kedvezményes partnerrel és ellenőrizd, hogy a számla összeg pontosan 15% kedvezményt tükröz',
            de: 'Erstelle eine Miete mit einem rabattierten Partner und überprüfe den korrekten 15%-Rabatt auf der Rechnung',
            uk: 'Створіть оренду з партнером зі знижкою та перевірте, що сума рахунку відображає точно 15% знижку',
          },
          {
            en: 'Verify the discount is applied correctly for rental, sales, and service categories separately',
            hu: 'Ellenőrizd, hogy a kedvezmény helyesen alkalmazódik bérlés, értékesítés és szerviz kategóriákra külön-külön',
            de: 'Überprüfe die korrekte Rabattanwendung für Miete, Verkauf und Service separat',
            uk: 'Перевірте правильне застосування знижки для категорій оренди, продажу та сервісу окремо',
          },
        ],
        expected: {
          en: 'Discount is capped at 15% maximum, warning is displayed for higher values, and category-specific discounts apply correctly.',
          hu: 'A kedvezmény maximum 15%-nál van korlátozva, figyelmeztetés jelenik meg magasabb értékeknél, és a kategória-specifikus kedvezmények helyesen alkalmazódnak.',
          de: 'Rabatt wird auf maximal 15% begrenzt, Warnung bei höheren Werten, kategoriespezifische Rabatte werden korrekt angewendet.',
          uk: 'Знижка обмежена максимум 15%, попередження відображається для вищих значень, категорійні знижки застосовуються коректно.',
        },
      },
    ],
  },
  {
    version: '4.0.0',
    date: '2026-03-25',
    features: [
      {
        nameEn: 'Release Hub — Tab Navigation',
        nameHu: 'Release Hub — Fül Navigáció',
        steps: [
          {
            en: 'Navigate to /admin-center/release-hub',
            hu: 'Navigálj az /admin-center/release-hub oldalra',
          },
          {
            en: 'Verify the Changelog tab is visible and active by default',
            hu: 'Ellenőrizd, hogy a Változásnapló fül látható és alapból aktív',
          },
          {
            en: 'Click on "Release Testing" tab',
            hu: 'Kattints a "Release Tesztelés" fülre',
          },
          {
            en: 'Verify the test features list loads for v4.0.0',
            hu: 'Ellenőrizd, hogy a tesztelési funkciók listája betölt a v4.0.0-hoz',
          },
        ],
        expected: {
          en: 'Both tabs switch correctly, content loads without errors',
          hu: 'Mindkét fül helyesen vált, a tartalom hibamentesen tölt be',
        },
      },
    ],
  },
];
